import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, CreditCard, Lock, ArrowLeft, CheckCircle } from 'lucide-react';
import { getSubscriptionPlans, getMockUsers, saveMockUsers, getMockPayments, saveMockPayments, getMockNotifications, saveMockNotifications } from '../mock';
import { useToast } from '../hooks/use-toast';

const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const email = location.state?.email;
  const planId = location.state?.planId;
  const isRenewal = location.state?.renewal;
  
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [formData, setFormData] = useState({
    name: '',
    email: email || '',
    phone: '',
    upiId: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvv: ''
  });

  const plans = getSubscriptionPlans();
  const selectedPlan = plans.find(p => p.id === planId);

  useEffect(() => {
    if (!email || !planId || !selectedPlan) {
      navigate('/choose-plan');
    }
  }, [email, planId, selectedPlan, navigate]);

  const handlePayment = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate payment processing
    setTimeout(() => {
      // Mock payment success
      const transactionId = `pay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Update user subscription
      const users = getMockUsers();
      const userIndex = users.findIndex(u => u.email === email);
      
      if (userIndex !== -1) {
        const startDate = new Date();
        const expiryDate = new Date();
        
        if (selectedPlan.durationType === 'days') {
          expiryDate.setDate(expiryDate.getDate() + selectedPlan.duration);
        } else if (selectedPlan.durationType === 'month') {
          expiryDate.setMonth(expiryDate.getMonth() + selectedPlan.duration);
        } else if (selectedPlan.durationType === 'year') {
          expiryDate.setFullYear(expiryDate.getFullYear() + selectedPlan.duration);
        }

        users[userIndex].subscription = {
          status: 'active',
          plan: planId,
          startDate: startDate.toISOString(),
          expiryDate: expiryDate.toISOString(),
          autoRenew: false,
          paymentMethod: 'razorpay',
          customizations: null,
          paidVia: paymentMethod
        };

        saveMockUsers(users);

        // Save payment history
        const payments = getMockPayments();
        payments.push({
          id: `PAY_${Date.now()}`,
          userId: users[userIndex].id,
          planId: planId,
          amount: selectedPlan.price,
          currency: '₹',
          status: 'success',
          paymentMethod: 'razorpay',
          transactionId: transactionId,
          paymentDate: new Date().toISOString(),
          invoiceUrl: null
        });
        saveMockPayments(payments);

        // Add notification
        const notifications = getMockNotifications();
        notifications.push({
          id: `NOTIF_${Date.now()}`,
          userId: users[userIndex].id,
          type: 'payment_success',
          title: 'Payment Successful',
          message: `Your payment of ${selectedPlan.currency}${selectedPlan.price.toLocaleString('en-IN')} for ${selectedPlan.name} plan was successful!`,
          read: false,
          createdAt: new Date().toISOString()
        });
        saveMockNotifications(notifications);
      }

      setLoading(false);
      navigate('/payment-success', { 
        state: { 
          transactionId, 
          plan: selectedPlan,
          email 
        } 
      });
    }, 2000);
  };

  if (!selectedPlan) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-cyan-50 to-orange-50 p-4">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            MyVenueMate
          </h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-2">Complete Your Payment</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                  <h3 className="font-semibold text-lg text-gray-900 dark:text-foreground">{selectedPlan.name} Plan</h3>
                  <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">
                    {selectedPlan.durationType === 'days' 
                      ? `${selectedPlan.duration} days`
                      : `${selectedPlan.duration} ${selectedPlan.durationType}${selectedPlan.duration > 1 ? 's' : ''}`
                    }
                  </p>
                </div>

                <div className="space-y-2 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-muted-foreground">Base Price</span>
                    <span className="font-medium">{selectedPlan.currency}{selectedPlan.base_price ? selectedPlan.base_price.toLocaleString('en-IN') : Math.round(selectedPlan.price / 1.18).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-muted-foreground">GST (18%)</span>
                    <span className="font-medium">{selectedPlan.currency}{selectedPlan.gst_amount ? selectedPlan.gst_amount.toLocaleString('en-IN') : Math.round(selectedPlan.price - (selectedPlan.price / 1.18)).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Total (incl. GST)</span>
                    <span className="text-teal-600">
                      {selectedPlan.currency}{selectedPlan.price.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-500 dark:text-muted-foreground text-center">* Price inclusive of 18% GST</p>
                </div>

                <div className="flex items-center justify-center pt-4 border-t">
                  <Lock className="w-4 h-4 text-gray-400 dark:text-muted-foreground mr-2" />
                  <span className="text-xs text-gray-600 dark:text-muted-foreground">Secured by Razorpay</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Payment Details</CardTitle>
                <CardDescription>
                  This is a demo payment flow. No actual charges will be made.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePayment} className="space-y-6">
                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 dark:text-foreground">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+91 XXXXX XXXXX"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        disabled
                      />
                    </div>
                  </div>

                  {/* Payment Method Selection */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 dark:text-foreground">Payment Method</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('upi')}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          paymentMethod === 'upi'
                            ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20'
                            : 'border-gray-200 dark:border-border hover:border-gray-300 dark:border-border'
                        }`}
                      >
                        <div className="text-center">
                          <div className="text-2xl mb-2">📱</div>
                          <div className="text-sm font-medium">UPI</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('card')}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          paymentMethod === 'card'
                            ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20'
                            : 'border-gray-200 dark:border-border hover:border-gray-300 dark:border-border'
                        }`}
                      >
                        <div className="text-center">
                          <CreditCard className="w-6 h-6 mx-auto mb-2" />
                          <div className="text-sm font-medium">Card</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('netbanking')}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          paymentMethod === 'netbanking'
                            ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20'
                            : 'border-gray-200 dark:border-border hover:border-gray-300 dark:border-border'
                        }`}
                      >
                        <div className="text-center">
                          <div className="text-2xl mb-2">🏦</div>
                          <div className="text-sm font-medium">Net Banking</div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Payment Method Fields */}
                  {paymentMethod === 'upi' && (
                    <div>
                      <Label htmlFor="upiId">UPI ID *</Label>
                      <Input
                        id="upiId"
                        type="text"
                        placeholder="yourname@upi"
                        value={formData.upiId}
                        onChange={(e) => setFormData({...formData, upiId: e.target.value})}
                        required
                      />
                      <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1">
                        Enter your UPI ID (e.g., yourname@paytm, yourname@googlepay)
                      </p>
                    </div>
                  )}

                  {paymentMethod === 'card' && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="cardNumber">Card Number *</Label>
                        <Input
                          id="cardNumber"
                          type="text"
                          placeholder="1234 5678 9012 3456"
                          value={formData.cardNumber}
                          onChange={(e) => setFormData({...formData, cardNumber: e.target.value})}
                          maxLength="19"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="cardExpiry">Expiry Date *</Label>
                          <Input
                            id="cardExpiry"
                            type="text"
                            placeholder="MM/YY"
                            value={formData.cardExpiry}
                            onChange={(e) => setFormData({...formData, cardExpiry: e.target.value})}
                            maxLength="5"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="cardCvv">CVV *</Label>
                          <Input
                            id="cardCvv"
                            type="password"
                            placeholder="123"
                            value={formData.cardCvv}
                            onChange={(e) => setFormData({...formData, cardCvv: e.target.value})}
                            maxLength="3"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'netbanking' && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-lg text-center">
                      <p className="text-sm text-blue-800">
                        You will be redirected to your bank&apos;s secure payment page
                      </p>
                    </div>
                  )}

                  {/* Demo Notice */}
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Demo Mode:</strong> This is a mock payment. Click &quot;Pay Now&quot; to simulate a successful payment.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      className="flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
                    >
                      {loading ? (
                        'Processing...'
                      ) : (
                        <>
                          Pay {selectedPlan.currency}{selectedPlan.price.toLocaleString('en-IN')}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500 dark:text-muted-foreground">
            Powered by <span className="font-semibold text-teal-600">MYONK</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Payment;
